import { useMemo } from 'react';
import React from 'react';
import {
  EnhancedGroupedSection,
  FormSchemaConfig,
} from '../../types/form/schema.types';
import { FormState } from '../../types';
import { FileDisplay } from '../../components/FileDisplay';
import { Field } from '@tw/tw-component-library';

// ============= Component =============
export const useRespLawyerSchema = (formState: FormState): FormSchemaConfig => {
  // Extract the responsible lawyer data for dependency tracking
  // const responsibleLawyerData = formState.data?.responsible_lawyer;

  return useMemo<FormSchemaConfig>(() => {
    const isEditAllowed = Array.isArray(formState.data?.userAssignedSections)
      ? (formState.data?.userAssignedSections as string[]).includes(
          'resp_lawyer'
        )
      : false;
    // Safely access the responsible lawyer data
    // const responsibleLawyer =
    //   responsibleLawyerData?.[0]?.label || responsibleLawyerData || '';

    // Create the main section that matches EnhancedGroupedSection type
    const respLawyerSection: EnhancedGroupedSection = {
      // profile: {
      //   name: responsibleLawyer,
      //   variant: 'row',
      //   theme: 'blue',
      //   job: 'Responsible Lawyer',
      // },
      layout: 'single',
      isGrouped: true,
      isGroupedAccordion: false,
      sections: [
        {
          title: {
            text: 'Payment Authorisation - Responsible Lawyer',
          },
          layout: 'single',
          fields: [
            {
              type: 'radio',
              name: 'resp_approve',
              label: 'Do you wish to authorise this payment?',
              options: [
                {
                  value: 'yes',
                  label: 'Yes',
                  disabled: false,
                },
                {
                  value: 'no',
                  label: 'No',
                  disabled: false,
                },
              ],
              required: true,
              validate: (value) =>
                !value
                  ? 'Please select whether you authorize this payment'
                  : undefined,
              disabled:
                formState.completedSections?.includes('resp_lawyer') ||
                !isEditAllowed,
            },
            {
              type: 'textArea',
              name: 'resp_reject_details',
              label: 'Rejection Details',
              required: true,
              validate: (value) =>
                !value ? 'Please enter the rejection details' : undefined,
              disabled:
                formState.completedSections?.includes('resp_lawyer') ||
                !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'resp_approve',
                      operator: 'equals',
                      value: 'no',
                    },
                  ],
                },
              ],
            },
            {
              type: 'checkbox',
              name: 'resp_verify_details',
              label:
                'I have reviewed the underlying transaction documentation and confirm it is consistent with the payment request:',
              options: [
                {
                  value: 'verified',
                  label: 'Acknowledge',
                  disabled: false,
                },
              ],
              required: true,
              validate: (value, formData) =>
                formData?.resp_approve === 'yes' && !value
                  ? 'Please acknowledge if you have reviewed the documentation'
                  : undefined,
              disabled:
                formState.completedSections?.includes('resp_lawyer') ||
                !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'resp_approve',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              ],
            },
            {
              type: 'checkbox',
              name: 'resp_verify_bank',
              label: 'I am satisfied that the bank details have been verified:',
              options: [
                {
                  value: 'verified',
                  label: 'Acknowledge',
                  disabled: false,
                },
              ],
              required: true,
              validate: (value, formData) =>
                formData?.resp_approve === 'yes' && !value
                  ? 'Please acknowledge if you have reviewed the bank details'
                  : undefined,
              disabled:
                formState.completedSections?.includes('resp_lawyer') ||
                !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'resp_approve',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              ],
            },
            {
              type: 'tagPicker',
              name: 'resp_bank_confirmed_by',
              label: 'Bank Details Confirmed By',
              disabled:
                formState.completedSections?.includes('resp_lawyer') ||
                !isEditAllowed,
              required: true,
              validate: (value, formData) =>
                formData?.resp_approve === 'yes' &&
                (!value || (Array.isArray(value) && value.length === 0))
                  ? 'Detailed confirmed by is required'
                  : undefined,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'resp_approve',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              ],
              extras: {
                tagPicker: {
                  minSearchLength: 4,
                  maxTags: 1,
                  createNewTags: false,
                },
              },
            },
            {
              type: 'checkbox',
              name: 'resp_verify_currency',
              label: 'I have confirmed the transaction currency:',
              options: [
                {
                  value: 'verified',
                  label: 'Acknowledge',
                  disabled: false,
                },
              ],
              required: true,
              validate: (value, formData) =>
                formData?.resp_approve === 'yes' && !value
                  ? 'Please acknowledge if you have confirmed the transaction currency'
                  : undefined,
              disabled:
                formState.completedSections?.includes('resp_lawyer') ||
                !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'resp_approve',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              ],
            },
            // Show existing files when form is completed
            ...(formState.completedSections?.includes('resp_lawyer') &&
            formState.data?.resp_attachment
              ? [
                  {
                    type: 'customComponent' as const,
                    customComponent: React.createElement(FileDisplay, {
                      fileName: formState.data?.resp_attachment,
                      workflowId: formState.data?.id,
                    }),
                    dependencies: [
                      {
                        type: 'show' as const,
                        logicalOperator: 'AND' as const,
                        conditions: [
                          {
                            field: 'resp_approve',
                            operator: 'equals' as const,
                            value: 'yes',
                          },
                        ],
                      },
                    ],
                    name: 'resp_attachment_display',
                    label: 'Attachment:',
                  },
                ]
              : []),
            // Show existing files when rejected (but still allow uploads)
            ...(formState.isRejected && formState.data?.resp_attachment
              ? [
                  {
                    type: 'customComponent' as const,
                    customComponent: React.createElement(FileDisplay, {
                      fileName: formState.data?.resp_attachment,
                      workflowId: formState.data?.id,
                    }),
                    dependencies: [
                      {
                        type: 'show' as const,
                        logicalOperator: 'AND' as const,
                        conditions: [
                          {
                            field: 'resp_approve',
                            operator: 'equals' as const,
                            value: 'yes',
                          },
                        ],
                      },
                    ],
                    name: 'resp_attachment_display_rejected',
                    label: 'Previously Uploaded Attachment:',
                  },
                ]
              : []),
            // Always show file upload field (unless form is completed and not rejected)
            ...(!formState.completedSections?.includes('resp_lawyer') ||
            formState.isRejected
              ? [
                  {
                    type: 'file' as const,
                    name: 'resp_attachment',
                    label: formState.isRejected
                      ? 'Upload Additional Attachment (if needed):'
                      : 'Attachment',
                    tooltip: 'Document Viewer only supports: .pdf',
                    extras: {
                      file: {
                        multiple: true,
                      },
                    } as Field['extras'],
                    disabled:
                      (formState.completedSections?.includes('resp_lawyer') &&
                        !formState.isRejected) ||
                      !isEditAllowed,
                    dependencies: [
                      {
                        type: 'show' as const,
                        logicalOperator: 'AND' as const,
                        conditions: [
                          {
                            field: 'resp_approve',
                            operator: 'equals' as const,
                            value: 'yes',
                          },
                        ],
                      },
                    ],
                  },
                ]
              : []),
            {
              type: 'radio',
              name: 'resp_hold_payment',
              label: `Hold Payment (awaiting funds/satisfaction of completion conditions)? If held the payment request is returned to the Responsible Lawyer to submit when payment is ready to action`,
              options: [
                {
                  value: 'yes',
                  label: 'Yes',
                  disabled: false,
                },
                {
                  value: 'no',
                  label: 'No',
                  disabled: false,
                },
              ],
              required: true,
              validate: (value, formData) =>
                formData?.resp_approve === 'yes' && !value
                  ? 'Please confirm if you wish to hold payment'
                  : undefined,
              disabled:
                formState.completedSections?.includes('resp_lawyer') ||
                !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'resp_approve',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              ],
            },
            {
              type: 'textArea',
              name: 'resp_comments',
              label: 'Additional Comments',
              disabled:
                formState.completedSections?.includes('resp_lawyer') ||
                !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'resp_approve',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
      extras: { grouped: true, evenSpacing: true },
    };

    return {
      sections: [respLawyerSection],
      dependencies: {
        showWhen: [
          {
            field: 'payment_details',
          },
        ],
      },
    };
  }, [formState]);
};
